import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Bold, Italic, Link, ListOrdered, List, Code, Send, Type, X, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import EmojiPicker from '@/components/inbox/components/team-chat/EmojiPicker';
import GifPicker from '@/components/inbox/components/team-chat/GifPicker';
import { Attachment as TaskAttachment } from './types';

interface TaskMessageComposerProps {
  message: string;
  setMessage: (message: string) => void;
  onSend: () => void;
  onAttachClick: () => void;
  pendingAttachments: TaskAttachment[];
  onRemoveAttachment: (index: number) => void;
  onAddAttachment?: (attachment: TaskAttachment) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFilesSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const TaskMessageComposer: React.FC<TaskMessageComposerProps> = ({
  message,
  setMessage,
  onSend,
  onAttachClick,
  pendingAttachments,
  onRemoveAttachment,
  onAddAttachment,
  fileInputRef,
  onFilesSelected,
}) => {
  const [showFormatting, setShowFormatting] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertFormat = (format: string) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = message;

    let before = text.substring(0, start);
    let selection = text.substring(start, end);
    let after = text.substring(end);

    let newText = "";

    switch (format) {
      case 'bold':
        newText = `${before}**${selection || 'bold'}**${after}`;
        break;
      case 'italic':
        newText = `${before}_${selection || 'italic'}_${after}`;
        break;
      case 'link':
        newText = `${before}[${selection || 'text'}](url)${after}`;
        break;
      case 'list-ol':
        newText = `${before}\n1. ${selection || 'item'}${after}`;
        break;
      case 'list-ul':
        newText = `${before}\n- ${selection || 'item'}${after}`;
        break;
      case 'code':
        newText = `${before}\`${selection || 'code'}\`${after}`;
        break;
      default:
        return;
    }

    setMessage(newText);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = message;

    const newText = text.substring(0, start) + emoji + text.substring(end);
    setMessage(newText);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleGifSelect = (gif: { id: string; title: string; url: string; originalUrl: string }) => {
    // For GIFs, we'll add them as external attachments (not uploaded to R2)
    const gifAttachment: TaskAttachment = {
      id: `gif_${Date.now()}`,
      name: gif.title || 'GIF',
      size: 0,
      type: 'image/gif',
      url: gif.url,
      dataUrl: gif.url,
      isExternalGif: true
    };
    onAddAttachment?.(gifAttachment);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [message]);

  const isSendingDisabled = !message.trim() && pendingAttachments.length === 0;

  return (
    <div className="relative">
      <div className="border transition-all duration-200 relative rounded-lg border-input bg-card dark:bg-background shadow-sm">
        {/* Formatting Toolbar - Top */}
        {showFormatting && (
          <div className="flex items-center px-2 py-1.5 border-b overflow-x-auto border-border bg-background/40">
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:bg-accent text-muted-foreground"
                onClick={() => insertFormat('bold')}
                title="Bold"
                type="button"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:bg-accent text-muted-foreground"
                onClick={() => insertFormat('italic')}
                title="Italic"
                type="button"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:bg-accent text-muted-foreground"
                onClick={() => insertFormat('link')}
                title="Link"
                type="button"
              >
                <Link className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:bg-accent text-muted-foreground"
                onClick={() => insertFormat('list-ol')}
                title="Ordered List"
                type="button"
              >
                <ListOrdered className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:bg-accent text-muted-foreground"
                onClick={() => insertFormat('list-ul')}
                title="Bullet List"
                type="button"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:bg-accent text-muted-foreground"
                onClick={() => insertFormat('code')}
                title="Code"
                type="button"
              >
                <Code className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Attachments Preview */}
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 px-3 pt-3">
            {pendingAttachments.map((a, i) => {
              const isImage = a.type?.startsWith('image/') || a.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
              return (
                <div key={`${a.id}-${i}`} className="flex items-center gap-3 p-2 rounded-md border max-w-[280px] bg-card border-border">
                  <div className="w-10 h-10 rounded-md overflow-hidden flex items-center justify-center shrink-0 border relative bg-muted border-border">
                    {isImage && a.dataUrl ? (
                      <img src={a.dataUrl} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <FileText className="w-5 h-5 opacity-50 text-muted-foreground" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-medium truncate text-foreground" title={a.name}>{a.name}</span>
                        {a.isExternalGif && (
                          <span className="text-[10px] text-muted-foreground">External GIF</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemoveAttachment(i)}
                        className="hover:text-red-500 transition-colors shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Textarea */}
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message"
          className="border-0 resize-none min-h-[44px] max-h-[200px] focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground bg-transparent"
          rows={1}
        />

        {/* Bottom Toolbar */}
        <div className="flex items-center justify-between px-2 py-1.5 border-t border-border">
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-7 w-7", showFormatting ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent")}
              onClick={() => setShowFormatting(!showFormatting)}
              title={showFormatting ? "Hide formatting" : "Show formatting"}
              type="button"
            >
              <Type className="h-4 w-4" />
            </Button>
            <div className="w-px h-4 mx-1 bg-border" />
            <EmojiPicker onSelect={handleEmojiSelect} side="top" isGlobalDarkMode={false} />
            <GifPicker onSelect={handleGifSelect} isGlobalDarkMode={false} />
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              onChange={onFilesSelected}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:bg-accent"
              onClick={onAttachClick}
              title="Attach files"
              type="button"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </Button>
          </div>
          <Button
            className="h-9 px-4 rounded-md transition-all duration-200 flex items-center gap-2 bg-workspace-accent hover:bg-workspace-accent-hover text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onSend}
            disabled={isSendingDisabled}
            type="button"
          >
            <Send className="h-3.5 w-3.5" />
            <span className="text-xs font-bold">Send</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskMessageComposer;
